import {
  ActionReducerMapBuilder,
  createAction,
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import axios from "../../axios";
import { ResetApiState, ResponseWrapper } from "../../redux.type";
import {
  Ticket,
  CancelationTicket,
  TicketStateTypes,
  TicketPrint,
} from "./Ticket.util";

const name = "tickets";
const extraActions = createExtraActions();

const TicketInitialState: TicketStateTypes = {
  tickets: ResetApiState([]),
  cancelationList: ResetApiState([]),

  cancelTicket: ResetApiState(null),
  requestRefundByOperation: ResetApiState(null),
  resetCancelTicket: ResetApiState(null),

  ticketPrint: ResetApiState(null),
  resetTicketPrint: ResetApiState(null),

  resetStates: ResetApiState(null),
};

/**
 * ACTIONS
 */
function createExtraActions() {
  const getTickets = createAsyncThunk<Ticket[], any>(
    `/get-${name}s`,
    async (date: any, thunkApi) => {
      return axios
        .get(`/${name}/date/${date}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const getTicketPrint = createAsyncThunk<TicketPrint | null, any>(
    `/get-${name}-print`,
    async (ticketId: any, thunkApi) => {
      return axios
        .get(`/${name}/one/${ticketId}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const resetTicketPrint = createAction(`/reset-${name}-ticket-print`);

  const getCancellationList = createAsyncThunk<CancelationTicket[], any>(
    `/get-cancellation-${name}s`,
    async (trip_id: any, thunkApi) => {
      return axios
        .get(`/${name}/ticket-cancellation/${trip_id}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const requestRefundByOperation = createAsyncThunk<
    ResponseWrapper<Ticket>,
    any
  >("/refunds/request-cancellation/operation", async (data: any, thunkApi) => {
    console.log("[Refund Thunk] Request data:", data);

    try {
      const response = await axios.post(
        `/refunds/request-cancellation/operation`,
        data
      );

      console.log("[Refund Thunk] Response received:", response);

      // Always return response.data
      return response.data;
    } catch (error: any) {
      console.error("[Refund Thunk] Caught error:", error);

      let errMsg = "Unknown error";

      // Trace error step by step
      if (error?.response) {
        console.log("[Refund Thunk] error.response:", error.response);

        if (error.response.data) {
          console.log(
            "[Refund Thunk] error.response.data:",
            error.response.data
          );

          if (typeof error.response.data === "object") {
            errMsg =
              error.response.data.message ||
              JSON.stringify(error.response.data);
          } else {
            errMsg = error.response.data;
          }
        }
      } else if (error?.message) {
        errMsg = error.message;
      }

      console.log("[Refund Thunk] Final error message:", errMsg);

      return thunkApi.rejectWithValue({ message: errMsg });
    }
  });

  const cancelTicket = createAsyncThunk<ResponseWrapper<Ticket>, any>(
    `/${name}`,
    async (data: any, thunkApi) =>
      axios
        .post(`/refunds/cancel-ticket`, data)
        .then((response) => {
          if (response.data.success) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetCancelTicket = createAction(`/reset-${name}-ticket-cancellation`);

  const resetStates = createAction(`/reset-states`);

  return {
    getTickets,
    getCancellationList,
    cancelTicket,
    requestRefundByOperation,
    resetCancelTicket,
    getTicketPrint,
    resetTicketPrint,
    resetStates,
  };
}

/**
 * REDUCERS
 */
function createExtraReducers(
  builder: ActionReducerMapBuilder<TicketStateTypes>
) {
  return {
    ...ticketsReducer(),
    ...cancellationListReducer(),
    ...cancelTicketReducer(),
    ...requestRefundByOperationReducer(),
    ...resetCancelTicket(),
    ...ticketPrintReducer(),
    ...resetTicketPrint(),
    ...resetStatesReducer(),
  };

  function ticketsReducer() {
    return {
      ...builder.addCase(extraActions.getTickets.pending, (state, action) => {
        state.tickets = {
          loading: true,
          payload: [],
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.getTickets.fulfilled, (state, action) => {
        state.tickets = {
          loading: false,
          payload: action.payload,
          successful: true,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.getTickets.rejected, (state, action) => {
        state.tickets = {
          loading: false,
          payload: [],
          successful: false,
          error: action.payload,
        };
      }),
    };
  }

  function ticketPrintReducer() {
    return {
      ...builder.addCase(
        extraActions.getTicketPrint.pending,
        (state, action) => {
          state.ticketPrint = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getTicketPrint.fulfilled,
        (state, action) => {
          state.ticketPrint = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getTicketPrint.rejected,
        (state, action) => {
          state.ticketPrint = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetTicketPrint() {
    return {
      ...builder.addCase(extraActions.resetTicketPrint, (state, action) => {
        state.resetTicketPrint = ResetApiState(true);
        state.ticketPrint = ResetApiState(null);
      }),
    };
  }

  function cancellationListReducer() {
    return {
      ...builder.addCase(
        extraActions.getCancellationList.pending,
        (state, action) => {
          state.cancelationList = {
            loading: true,
            payload: [],
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getCancellationList.fulfilled,
        (state, action) => {
          state.cancelationList = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getCancellationList.rejected,
        (state, action) => {
          state.cancelationList = {
            loading: false,
            payload: [],
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function cancelTicketReducer() {
    return {
      ...builder.addCase(extraActions.cancelTicket.pending, (state, action) => {
        state.cancelTicket = {
          loading: true,
          payload: null,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(
        extraActions.cancelTicket.fulfilled,
        (state, action) => {
          state.cancelTicket = {
            loading: false,
            payload: action.payload.message,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.cancelTicket.rejected,
        (state, action) => {
          state.cancelTicket = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function requestRefundByOperationReducer() {
    return {
      ...builder.addCase(
        extraActions.requestRefundByOperation.pending,
        (state: any, action: any) => {
          state.requestRefundByOperation = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.requestRefundByOperation.fulfilled,
        (state: any, action: any) => {
          state.requestRefundByOperation = {
            loading: false,
            payload: action.payload.message,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.requestRefundByOperation.rejected,
        (state: any, action: any) => {
          console.log("action requestRefundByOperation", action.payload);
          state.requestRefundByOperation = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload?.message || "Unknown error",
          };
        }
      ),
    };
  }

  function resetCancelTicket() {
    return {
      ...builder.addCase(extraActions.resetCancelTicket, (state, action) => {
        state.resetCancelTicket = ResetApiState(true);
        // state.cancelTicket = ResetApiState(null);
        state.requestRefundByOperation = ResetApiState(null);
      }),
    };
  }

  function resetStatesReducer() {
    return {
      ...builder.addCase(extraActions.resetStates, (state, action) => {
        state.resetStates = ResetApiState(true);

        state.tickets = ResetApiState([]);
      }),
    };
  }
}

const TicketSlice = createSlice({
  name,
  initialState: TicketInitialState,
  reducers: {},
  extraReducers: (builder) => createExtraReducers(builder),
});

export const TicketActions = { ...TicketSlice.actions, ...extraActions };
export const TicketReducer = TicketSlice.reducer;
